import { useEffect, useState } from 'react';
import * as React from 'react';

import {
  Button,
  CircularProgress,
  DialogContent,
  Fab,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Alert,
} from '@mui/material';
import Dialog from '@mui/material/Dialog';

import type { SubscriptionDTO, UserDTO } from '@klangio/ai2notes-ts-client';
import { UsersService } from '@klangio/ai2notes-ts-client';
import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import type { WithUser } from '../../../context';
import { withUserContext } from '../../../context';
import { SubscriptionState } from '../../../entities/subscription';

import WavyDialogBackground from '../../atoms/WavyDialogBackground';
import CancelSubscriptionOffer from './CancelSubscriptionOffer';

const useStyles = makeStyles()(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '1em',
    maxWidth: '30em',
  },
  gap: {
    margin: '1em 0',
  },
}));

export interface CancelSubscriptionDialogProps extends WithUser {
  open: boolean;
  onClose: () => void;
  subscriptionId: string;
}

const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  refresh,
  user,
  open,
  onClose,
  subscriptionId,
}) => {
  const { classes } = useStyles();
  const [selectedOption, setSelectedOption] = useState('');
  const [freeTextValue, setFreeTextValue] = useState('');
  const [fieldError, setFieldError] = useState(false);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(false);
  const [success, setSuccess] = useState(false);
  const [discountDialog, setDiscountDialog] = useState(false);
  const { t } = useTranslation();

  const findSubscription = (updatedUser: UserDTO) => {
    return updatedUser?.subscriptions?.find((sub: SubscriptionDTO) => subscriptionId === sub.id);
  };
  const [subscription, setSubscription] = useState(findSubscription(user));

  useEffect(() => {
    let intervalId: number = null;
    if (user?.subscriptions?.length !== 0 && polling) {
      intervalId = window.setInterval(() => {
        refresh().then((updatedUser) => {
          const updatedSubscription = findSubscription(updatedUser);
          // @ts-ignore
          if (updatedSubscription?.state === SubscriptionState.CANCELLATION_REQUESTED) {
            setSubscription(updatedSubscription);
            setSuccess(true);
            setPolling(false);
            window.clearInterval(intervalId);
          }
        });
      }, 2000);
    }
    return () => window.clearInterval(intervalId);
  }, [user, polling, refresh]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
    setFieldError(false);
  };

  const handleFreeTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFreeTextValue(event.target.value);
  };

  const onSubmit = async (forceCancellation?: Boolean) => {
    if (
      selectedOption === 'expensive'
      // @ts-ignore
      && (!subscription?.discount || subscription?.discount === 0)
      && !forceCancellation
    ) {
      setDiscountDialog(true);
      return;
    }

    setFieldError(false);

    if (!selectedOption) {
      setFieldError(true);
      return;
    }
    const formData = {
      subscriptionId: subscription.providerId,
      reason: selectedOption,
      description: freeTextValue,
    };

    try {
      await UsersService.usersCancelSubscription(formData);
      setPolling(true);
    } catch {
      setError(true);
    }
  };

  const handleClose = () => {
    setDiscountDialog(false);
    onClose();
  };

  if (success) {
    return (
      <Dialog open={open} onClose={onClose}>
        <WavyDialogBackground />
        <DialogContent className={classes.content}>
          <Typography align="center" variant="h5">
            <strong>{t('dialog.cancel_subscription.title')}</strong>
          </Typography>
          <div className={classes.gap}>
            <Alert>{t('dialog.cancel_subscription.succeed')}</Alert>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose}>
      <WavyDialogBackground />
      <DialogContent className={classes.content}>
        {discountDialog && (
          <CancelSubscriptionOffer
            open={discountDialog}
            onClose={handleClose}
            onError={() => setError(true)}
            onSubmit={() => onSubmit(true)}
            providerId={subscription.providerId}
          />
        )}
        {!discountDialog && (
          <>
            <Typography variant="h5" align="center">
              <strong>{t('dialog.cancel_subscription.title')}</strong>
            </Typography>
            <Typography variant="body1" align="center">
              {t('dialog.cancel_subscription.subtitle')}
            </Typography>
            <FormControl fullWidth required>
              <InputLabel error={fieldError} htmlFor="reason" id="reason-label">
                {t('dialog.cancel_subscription.reason_label')}
              </InputLabel>
              <Select
                fullWidth
                name="reason"
                id="reason"
                defaultValue=""
                value={selectedOption}
                onChange={handleChange}
              >
                <MenuItem value="expensive">
                  {t('dialog.cancel_subscription.reasons.expensive')}
                </MenuItem>
                <MenuItem value="noUse">{t('dialog.cancel_subscription.reasons.noUse')}</MenuItem>
                <MenuItem value="badQuality">
                  {t('dialog.cancel_subscription.reasons.badQuality')}
                </MenuItem>
                <MenuItem value="technicalIssues">
                  {t('dialog.cancel_subscription.reasons.technicalIssues')}
                </MenuItem>
                <MenuItem value="anotherProduct">
                  {t('dialog.cancel_subscription.reasons.anotherProduct')}
                </MenuItem>
                <MenuItem value="pause">{t('dialog.cancel_subscription.reasons.pause')}</MenuItem>
                <MenuItem value="other">{t('dialog.cancel_subscription.reasons.other')}</MenuItem>
              </Select>
              {fieldError && <FormHelperText error>{t('common.required')}</FormHelperText>}
            </FormControl>

            {selectedOption === 'other' ? (
              <TextField
                id="otherReason"
                onChange={handleFreeTextChange}
                label={t('dialog.cancel_subscription.free_text_reason')}
                value={freeTextValue}
                inputProps={{ maxLength: 300 }}
                fullWidth
                type="text"
              />
            ) : (
              <TextField
                fullWidth
                id="freetext"
                onChange={handleFreeTextChange}
                multiline
                inputProps={{ maxLength: 300 }}
                label={t('dialog.cancel_subscription.free_text_label')}
                value={freeTextValue}
                type="text"
              />
            )}
            {polling ? (
              <div className={classes.gap}>
                <CircularProgress size={60} />
                <br />
              </div>
            ) : (
              <>
                <Button onClick={() => onSubmit(false)}>
                  {t('dialog.cancel_subscription.submit')}
                </Button>
                <Fab onClick={onClose} variant="extended" color="primary">
                  {t('dialog.cancel_subscription.later')}
                </Fab>
              </>
            )}
          </>
        )}
        {error && <Alert severity="error">{t('common.error')}</Alert>}
      </DialogContent>
    </Dialog>
  );
};

export default withUserContext(CancelSubscriptionDialog);
